<template>
  <v-dialog :value="value" @input="$emit('input', $event)" max-width="720px" persistent>
    <v-card>
      <v-card-title class="title">AI Analysis · {{ label }}</v-card-title>
      <v-card-text class="body-2 pt-4">
        <v-textarea
          v-model="text"
          outlined
          auto-grow
          rows="4"
          hide-details
          :placeholder="`Short paragraph about ${label} performance…`"
        ></v-textarea>
      </v-card-text>
      <v-card-actions>
        <v-btn color="red" id="close" text @click.native="$emit('input', false)">Close</v-btn>
        <v-spacer></v-spacer>
        <v-btn color="secondary" text :loading="generating" :disabled="!posts || !posts.length" @click="generate">
          <i class="fas fa-magic mr-2"></i> Generate with AI
        </v-btn>
        <v-btn color="green" text :loading="saving" @click="save">Save</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import campaignAPIs from './campaignAPIs'

/**
 * Per-summary-table AI analysis dialog. Opened from each summary section's
 * header (next to the column-settings and download buttons). Generates ONE
 * short paragraph (Claude via AWS Bedrock — routed through campaign_service ->
 * ims_ai_assistant_service, NOT OpenAI) over that table's rows, editable here
 * and stored under campaign.AITableSummaries[typeKey].
 *
 * IMPORTANT (correctness): `typeKey` is the CANONICAL section key
 * ('post','reel','story','tiktok','youtube','facebook','xhs','lemon'), NOT the
 * per-post flat type. IG Stories in particular arrive here as two flat types
 * ('story' + 'gdrive'/multiple-contents) but share ONE canonical key 'story',
 * so a single paragraph covers the whole IG Story table. The Kobe HTML report
 * (buildReportHtml.js AI_TABLE_KEY) maps each flat report type back onto this
 * canonical key when rendering the paragraph under the table title.
 */
export default {
  name: 'TableAiAnalysisModal',
  props: {
    value: Boolean, // dialog v-model
    campaign: Object, // holds saved AITableSummaries
    posts: Array, // this section's already-flattened analysis rows (calculatePostData output)
    typeKey: String, // canonical section key, e.g. 'reel', 'story', 'tiktok'
    label: String // display label, e.g. 'IG Reel'
  },
  data () {
    return {
      text: '',
      generating: false,
      saving: false
    }
  },
  watch: {
    value: {
      immediate: true,
      handler (open) {
        if (open) {
          const saved = (this.campaign && this.campaign.AITableSummaries) || {}
          this.text = saved[this.typeKey] || ''
        }
      }
    }
  },
  methods: {
    // Compact per-post rows for the prompt — only meaningful numeric fields.
    // Reads the flattened (snake_case) fields produced by calculatePostData,
    // the same fields the Kobe report's tables render, so the AI sees exactly
    // what the client sees.
    rowsContext () {
      const n = v => (v === null || v === undefined || v === '' ? 0 : Number(v) || 0)
      const rows = (this.posts || []).map(p => {
        const row = {
          creator: p.name || p.instagram || 'unknown',
          liveDate: p.live_date,
          audienceSize: n(p.outreach)
        }
        const add = (key, val) => { if (n(val)) row[key] = n(val) }
        add('reach', p.reach)
        add('impressions', p.impressions)
        add('plays', p.views)
        add('likes', p.likes !== undefined ? p.likes : p.like_count)
        add('comments', p.comments !== undefined ? p.comments : p.comment_count)
        add('shares', p.shares)
        add('saves', p.saved !== undefined ? p.saved : p.bookmarks)
        add('replies', p.replies) // stories
        add('linkClicks', p.link_clicks) // stories
        add('engagementRatePct', p.new_engagement_rate)
        return row
      })
      return {
        campaign: (this.campaign && this.campaign.name) || 'Campaign',
        contentType: this.label,
        contents: rows.length,
        rows: rows.slice(0, 60)
      }
    },
    async generate () {
      this.generating = true
      try {
        const resp = await campaignAPIs.getCampaignAISummary({
          campaignContext: JSON.stringify(this.rowsContext()),
          mode: 'table' // short single paragraph (TABLE_ANALYSIS_PROMPT)
        })
        if (resp && resp.analysis) this.text = String(resp.analysis).trim()
      } catch (e) {
        console.error('table AI generate failed', e)
        this.$swal({ text: 'Could not generate the analysis. Try again.', type: 'error' })
      } finally {
        this.generating = false
      }
    },
    async save () {
      this.saving = true
      try {
        // merge with the other sections' saved paragraphs — never wipe them
        const merged = Object.assign({}, (this.campaign && this.campaign.AITableSummaries) || {})
        if (this.text && this.text.trim()) merged[this.typeKey] = this.text.trim()
        else delete merged[this.typeKey]
        const formData = new FormData()
        formData.append('data', JSON.stringify({ AITableSummaries: merged }))
        await campaignAPIs.updateCampaignSummary(this.campaign.id, formData)
        this.$set(this.campaign, 'AITableSummaries', merged)
        this.$emit('saved', merged)
        this.$emit('input', false)
      } catch (e) {
        console.error('table AI save failed', e)
        this.$swal({ text: 'Could not save the analysis.', type: 'error' })
      } finally {
        this.saving = false
      }
    }
  }
}
</script>




// WEBPACK FOOTER //
// TableAiAnalysisModal.vue?5eaa02c5